/**
 * 装备系统引擎：随机生成、强化、查询
 */

import type { PlayerState } from './Player';
import type { EquipSlot, Quality, StatRange } from '../data/equipment';
import { EQUIP_TEMPLATES, QUALITY_MULT, QUALITY_NAMES, type EquipTemplate } from '../data/equipment';

export interface Equipment {
  id: string;
  name: string;
  slot: EquipSlot;
  quality: Quality;
  attackBonus: number;
  defenseBonus: number;
  hpBonus: number;
  mpBonus: number;
  capacity?: number;
  enhanceLevel: number;
  isPerfect: boolean;
  /** 随机roll值 */
  rollPct: number;
}

/** 随机roll一个属性值 */
function rollStat([min, max]: [number, number], qualityMult: number): { value: number; pct: number } {
  const base = min + Math.random() * (max - min);
  const rolled = base * qualityMult * (0.7 + Math.random() * 0.6);
  const maxPossible = max * qualityMult * 1.3;
  const pct = maxPossible > 0 ? rolled / maxPossible : 1;
  return { value: Math.floor(rolled), pct };
}

/** 从模板生成装备实例 */
export function generateEquipment(templateId: string): Equipment | null {
  const tmpl = EQUIP_TEMPLATES.find((t) => t.id === templateId);
  if (!tmpl) return null;

  const qMult = QUALITY_MULT[tmpl.quality];
  const atk = tmpl.baseStats.attack ? rollStat(tmpl.baseStats.attack, qMult) : { value: 0, pct: 0 };
  const def = tmpl.baseStats.defense ? rollStat(tmpl.baseStats.defense, qMult) : { value: 0, pct: 0 };
  const hp = tmpl.baseStats.hp ? rollStat(tmpl.baseStats.hp, qMult) : { value: 0, pct: 0 };
  const mp = tmpl.baseStats.mp ? rollStat(tmpl.baseStats.mp, qMult) : { value: 0, pct: 0 };

  const allPcts = [atk.pct, def.pct, hp.pct, mp.pct].filter((p) => p > 0);
  const avgPct = allPcts.length > 0 ? allPcts.reduce((a, b) => a + b, 0) / allPcts.length : 0;
  const isPerfect = avgPct >= 0.9;

  return {
    id: tmpl.id,
    name: tmpl.name,
    slot: tmpl.slot,
    quality: tmpl.quality,
    attackBonus: atk.value,
    defenseBonus: def.value,
    hpBonus: hp.value,
    mpBonus: mp.value,
    capacity: tmpl.capacity,
    enhanceLevel: 0,
    isPerfect,
    rollPct: Math.floor(avgPct * 100),
  };
}

/** 强化成功率 */
export function getEnhanceRate(level: number): number {
  const rates = [1, 0.95, 0.90, 0.80, 0.70, 0.60, 0.50, 0.40, 0.30, 0.20, 0.15, 0.10, 0.05];
  return rates[Math.min(level, 12)];
}

/** 强化费用 */
export function getEnhanceCost(equip: Equipment): { stones: number; cores: number } {
  const baseMap: Record<string, [number, number]> = {
    common: [50, 1], spirit: [100, 2], mystic: [200, 4],
    earth: [500, 8], heaven: [1000, 15], immortal: [3000, 30],
  };
  const [baseStones, baseCores] = baseMap[equip.quality] ?? [50, 1];
  const mult = 1 + equip.enhanceLevel * 0.5;
  return {
    stones: Math.floor(baseStones * mult),
    cores: Math.floor(baseCores * mult),
  };
}

/** 执行强化 */
export function enhanceEquipment(player: PlayerState, slot: EquipSlot): { ok: boolean; message: string; destroyed: boolean } {
  const equip = player.equipment[slot] as Equipment | undefined;
  if (!equip) return { ok: false, message: '该槽位没有装备', destroyed: false };
  if (equip.enhanceLevel >= 13) return { ok: false, message: '已达最高强化等级 +13', destroyed: false };

  const cost = getEnhanceCost(equip);
  if (player.spiritStones < cost.stones) return { ok: false, message: `灵石不足！需要 ${cost.stones}`, destroyed: false };
  const coreCount = player.inventory.filter((i) => i.id === 'monster_core').length;
  if (coreCount < cost.cores) return { ok: false, message: `妖丹不足！需要 ${cost.cores}`, destroyed: false };

  // 消耗材料
  player.spiritStones -= cost.stones;
  for (let i = 0; i < cost.cores; i++) {
    const idx = player.inventory.findIndex((inv) => inv.id === 'monster_core');
    if (idx !== -1) player.inventory.splice(idx, 1);
  }

  const rate = getEnhanceRate(equip.enhanceLevel);
  // 检查是否有保护符/幸运符
  const luckyIdx = player.inventory.findIndex((i) => i.id === 'enhance_lucky');
  const protectIdx = player.inventory.findIndex((i) => i.id === 'enhance_protect');
  const safeIdx = player.inventory.findIndex((i) => i.id === 'enhance_safe');
  const effRate = Math.min(0.95, rate + (luckyIdx !== -1 ? 0.15 : 0));

  if (luckyIdx !== -1) player.inventory.splice(luckyIdx, 1);

  if (Math.random() < effRate) {
    // 成功
    equip.enhanceLevel++;
    if (protectIdx !== -1) player.inventory.splice(protectIdx, 1);
    if (safeIdx !== -1) player.inventory.splice(safeIdx, 1);
    return { ok: true, message: `🔨 强化成功！${equip.name} → +${equip.enhanceLevel}！`, destroyed: false };
  } else {
    // 失败
    if (equip.enhanceLevel >= 10 && safeIdx === -1) {
      // 损毁
      delete player.equipment[slot];
      if (protectIdx !== -1) player.inventory.splice(protectIdx, 1);
      return { ok: false, message: `💔 ${equip.name} 强化失败，装备损毁了...`, destroyed: true };
    }

    // 掉级
    const drop = equip.enhanceLevel >= 8 ? 3 : equip.enhanceLevel >= 6 ? 2 : 1;
    equip.enhanceLevel = equip.enhanceLevel >= 10 ? 0 : Math.max(0, equip.enhanceLevel - drop);
    if (protectIdx !== -1) { player.inventory.splice(protectIdx, 1); }
    else { return { ok: false, message: `💥 强化失败！${equip.name} → +${equip.enhanceLevel}（${protectIdx !== -1 ? '保护符生效' : ''}）`, destroyed: false }; }
    return { ok: false, message: `💥 强化失败！${equip.name} 降至 +${equip.enhanceLevel}`, destroyed: false };
  }
}

function safeNum(v: any): number {
  if (typeof v !== 'number' || isNaN(v)) return 0;
  return v;
}

/** 获取最终属性（含强化加成） */
export function getFinalStats(equip: Equipment | any): { atk: number; def: number; hp: number; mp: number; cap?: number } {
  const level = safeNum(equip.enhanceLevel);
  const enhanceMult = Math.pow(1.08, level);
  return {
    atk: Math.floor(safeNum(equip.attackBonus) * enhanceMult),
    def: Math.floor(safeNum(equip.defenseBonus) * enhanceMult),
    hp: Math.floor(safeNum(equip.hpBonus) * enhanceMult),
    mp: Math.floor(safeNum(equip.mpBonus) * enhanceMult),
    cap: equip.capacity,
  };
}

/** 获取模板 */
export function getTemplate(id: string): EquipTemplate | undefined {
  return EQUIP_TEMPLATES.find((t) => t.id === id);
}

/** 获取槽位可装备列表 */
export function getEquippableForSlot(slot: EquipSlot): EquipTemplate[] {
  return EQUIP_TEMPLATES.filter((t) => t.slot === slot || (slot === 'ring2' && t.slot === 'ring1'));
}
